import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Plus, Pencil, Trash2, Save, X, DollarSign, BarChart3, LogOut, Crown, ExternalLink, Star, Wallet, ArrowUpRight, Clock, CheckCircle, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const FEATURED_PACKAGES = {
  spotlight_7: { name: "Spotlight 7", price: 9.99, days: 7, description: "7 days featured" },
  spotlight_30: { name: "Spotlight 30", price: 24.99, days: 30, description: "30 days featured", popular: true },
  spotlight_90: { name: "Spotlight 90", price: 49.99, days: 90, description: "90 days featured" },
};

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeView, setActiveView] = useState("products"); // products, payouts, featured
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", product_type: "app", price: 0, subscription_price: 0, image_url: "", download_url: "", demo_url: "", platforms: [], tags: [] });
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutDetails, setPayoutDetails] = useState("");
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [featureProduct, setFeatureProduct] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem("creator_token");
    if (!t) { navigate("/creator"); return; }
    setToken(t);
  }, [navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      const [p, prods, pays] = await Promise.all([
        api.getCreatorProfile(token),
        api.getCreatorProducts(token),
        api.getCreatorPayouts(token),
      ]);
      setProfile(p);
      setProducts(prods);
      setPayouts(pays);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("creator_token");
        navigate("/creator");
      }
    }
  }, [token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, price: parseFloat(form.price) || 0, subscription_price: parseFloat(form.subscription_price) || 0 };
      if (editing) await api.updateProduct(editing.id, data, token);
      else await api.createProduct(data, token);
      setShowForm(false); setEditing(null); resetForm(); loadData();
    } catch (err) { alert(err.response?.data?.detail || "Error saving product"); }
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || "", product_type: p.product_type, price: p.price || 0, subscription_price: p.subscription_price || 0, image_url: p.image_url || "", download_url: p.download_url || "", demo_url: p.demo_url || "", platforms: p.platforms || [], tags: p.tags || [] });
    setShowForm(true);
  };

  const handleDelete = async (id) => { if (window.confirm("Delete this product?")) { await api.deleteProduct(id, token); loadData(); } };
  const resetForm = () => setForm({ name: "", description: "", product_type: "app", price: 0, subscription_price: 0, image_url: "", download_url: "", demo_url: "", platforms: [], tags: [] });

  const handleRequestPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { alert("Enter a valid amount"); return; }
    setPayoutLoading(true);
    try {
      await api.requestPayout({ amount: amt, payment_method: "bank_transfer", payment_details: payoutDetails }, token);
      setPayoutAmount(""); setPayoutDetails(""); loadData();
    } catch (err) { alert(err.response?.data?.detail || "Payout request failed"); }
    finally { setPayoutLoading(false); }
  };

  const handleFeaturePurchase = async (packageId) => {
    if (!featureProduct) return;
    try {
      const result = await api.createCheckout({
        featured_package_id: packageId,
        featured_product_id: featureProduct.id,
        origin_url: window.location.origin,
      });
      window.location.href = result.url;
    } catch (err) { alert(err.response?.data?.detail || "Failed to initiate checkout"); }
  };

  const handleUpgradeTier = async (tierId) => {
    try {
      const result = await api.createCheckout({ tier_id: tierId, purchase_type: "subscription", origin_url: window.location.origin });
      window.location.href = result.url;
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const togglePlatform = (p) => setForm(prev => ({ ...prev, platforms: prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p] }));

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all text-sm";
  const tierInfo = { starter: { commission: "30%", color: "#A0A0AB" }, pro: { commission: "20%", color: "#00F0FF" }, enterprise: { commission: "15%", color: "#FF007F" }, nonprofit: { commission: "10%", color: "#4D00FF" } };
  const tier = tierInfo[profile?.tier] || tierInfo.starter;

  if (!profile) return <div className="min-h-screen bg-[#05050A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="creator-dashboard">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center text-white font-black text-lg font-['Unbounded']">
              {profile.name?.charAt(0)?.toUpperCase() || "C"}
            </div>
            <div>
              <h1 className="font-['Unbounded'] text-xl font-bold text-white">{profile.name}</h1>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full border font-['JetBrains_Mono']" style={{ borderColor: `${tier.color}50`, color: tier.color }}>{profile.tier?.toUpperCase()}</span>
                <span className="text-[#A0A0AB] text-xs">{tier.commission} commission</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/marketplace" className="text-[#A0A0AB] hover:text-[#00F0FF] text-sm flex items-center gap-1"><ExternalLink className="w-4 h-4" /> Marketplace</Link>
            <button data-testid="creator-logout" onClick={() => { localStorage.removeItem("creator_token"); navigate("/creator"); }} className="text-[#A0A0AB] hover:text-red-400 text-sm flex items-center gap-1"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { icon: Package, label: "Products", value: products.length, color: "#00F0FF" },
            { icon: DollarSign, label: "Total Earned", value: `$${(profile.total_revenue || 0).toFixed(2)}`, color: "#00F0FF" },
            { icon: Wallet, label: "Available", value: `$${(profile.available_balance || 0).toFixed(2)}`, color: "#4D00FF" },
            { icon: BarChart3, label: "Sales", value: profile.total_sales || 0, color: "#FF007F" },
            { icon: ArrowUpRight, label: "Paid Out", value: `$${(profile.total_paid_out || 0).toFixed(2)}`, color: "#A0A0AB" },
          ].map((s) => (
            <div key={s.label} className="glass-card rounded-2xl p-4">
              <s.icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
              <p className="text-xl font-bold text-white">{s.value}</p>
              <p className="text-[#A0A0AB] text-xs">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Upgrade Banner */}
        {profile.tier === "starter" && (
          <div className="glass-card rounded-2xl p-5 mb-8 border-[#00F0FF]/20 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-white font-semibold text-sm">Upgrade for unlimited listings & lower commission</h3>
              <p className="text-[#A0A0AB] text-xs">Pro: 20% | Enterprise: 15%</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleUpgradeTier("pro")} className="neon-btn text-xs py-2 px-4">Pro $19.99/mo</button>
              <button onClick={() => handleUpgradeTier("enterprise")} className="neon-btn neon-btn-pink text-xs py-2 px-4">Enterprise $49.99/mo</button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: "products", label: "Products", icon: Package },
            { id: "payouts", label: "Payouts", icon: Wallet },
            { id: "featured", label: "Boost Product", icon: Star },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`creator-tab-${t.id}`}
              onClick={() => setActiveView(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeView === t.id ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" : "bg-white/5 text-[#A0A0AB] border border-white/10"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Products View */}
        {activeView === "products" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Unbounded'] text-lg font-bold text-white">Your Products</h2>
              <button data-testid="add-product-btn" onClick={() => { setShowForm(true); setEditing(null); resetForm(); }} className="neon-btn flex items-center gap-2 text-xs py-2 px-5"><Plus className="w-4 h-4" /> Add Product</button>
            </div>

            {showForm && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-6 mb-8" data-testid="product-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-['Unbounded'] text-base font-semibold text-white">{editing ? "Edit" : "New"} Product</h3>
                  <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-[#A0A0AB] hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">Name</label><input data-testid="product-name" className={inputClass} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Product name" /></div>
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">Type</label>
                    <select data-testid="product-type" className={inputClass} value={form.product_type} onChange={(e) => setForm({...form, product_type: e.target.value})}>
                      <option value="app">App</option><option value="music">Music</option><option value="video">Video</option><option value="media">Media</option><option value="service">Service</option>
                    </select></div>
                  <div className="md:col-span-2"><label className="text-xs text-[#A0A0AB] mb-1 block">Description</label><textarea className={inputClass + " resize-none"} rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Describe your product" /></div>
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">One-Time Price ($)</label><input type="number" step="0.01" min="0" className={inputClass} value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} /></div>
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">Monthly Sub ($)</label><input type="number" step="0.01" min="0" className={inputClass} value={form.subscription_price} onChange={(e) => setForm({...form, subscription_price: e.target.value})} /></div>
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">Image URL</label><input className={inputClass} value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="https://..." /></div>
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">Download URL</label><input className={inputClass} value={form.download_url} onChange={(e) => setForm({...form, download_url: e.target.value})} placeholder="https://..." /></div>
                  <div><label className="text-xs text-[#A0A0AB] mb-1 block">Platforms</label>
                    <div className="flex gap-2 mt-1">{["iOS", "Android", "Web", "Windows", "Mac"].map(p => (
                      <button key={p} type="button" onClick={() => togglePlatform(p)} className={`px-3 py-2 rounded-lg text-xs border transition-all ${form.platforms.includes(p) ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-[#A0A0AB]"}`}>{p}</button>
                    ))}</div></div>
                  <div className="md:col-span-2"><button data-testid="product-submit" type="submit" className="neon-btn flex items-center gap-2 text-sm py-2 px-6"><Save className="w-4 h-4" /> {editing ? "Update" : "Create"}</button></div>
                </form>
              </motion.div>
            )}

            <div className="space-y-4">
              {products.length === 0 ? (
                <div className="glass-card rounded-2xl p-8 text-center"><Package className="w-8 h-8 text-[#00F0FF] mx-auto mb-3" /><p className="text-[#A0A0AB] text-sm">No products yet.</p></div>
              ) : products.map((p) => (
                <div key={p.id} data-testid={`creator-product-${p.id}`} className="glass-card rounded-2xl p-5 flex items-center gap-5">
                  {p.image_url && <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold text-sm truncate">{p.name}</h3>
                      {p.is_featured && <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 shrink-0" />}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] font-['JetBrains_Mono'] uppercase text-[#00F0FF]">{p.product_type}</span>
                      {p.price > 0 && <span className="text-white text-xs font-bold">${p.price.toFixed(2)}</span>}
                      <span className="text-[#A0A0AB] text-xs">{p.total_sales || 0} sales</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => { setFeatureProduct(p); setActiveView("featured"); }} className="w-8 h-8 rounded-lg bg-yellow-400/5 flex items-center justify-center text-yellow-400/60 hover:text-yellow-400 transition-all" title="Boost"><Star className="w-4 h-4" /></button>
                    <button onClick={() => handleEdit(p)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-[#00F0FF] transition-all"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(p.id)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-red-400 transition-all"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Payouts View */}
        {activeView === "payouts" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-['Unbounded'] text-lg font-bold text-white mb-6">Payouts</h2>

            {/* Request Payout */}
            <div className="glass-card rounded-2xl p-6 mb-8" data-testid="payout-request-form">
              <h3 className="text-white font-semibold text-sm mb-4">Request Payout</h3>
              <div className="flex items-center gap-3 mb-4">
                <div className="text-[#4D00FF] text-2xl font-bold">${(profile.available_balance || 0).toFixed(2)}</div>
                <span className="text-[#A0A0AB] text-xs">available balance</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#A0A0AB] mb-1 block">Amount ($)</label>
                  <input data-testid="payout-amount" type="number" step="0.01" min="0" className={inputClass} value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label className="text-xs text-[#A0A0AB] mb-1 block">Payment Details (PayPal email / Bank info)</label>
                  <input data-testid="payout-details" className={inputClass} value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)} placeholder="your@paypal.com" />
                </div>
                <div className="flex items-end">
                  <button data-testid="payout-submit" onClick={handleRequestPayout} disabled={payoutLoading} className="neon-btn text-sm py-3 px-6 w-full disabled:opacity-50">
                    {payoutLoading ? "Requesting..." : "Request Payout"}
                  </button>
                </div>
              </div>
            </div>

            {/* Payout History */}
            <h3 className="text-white font-semibold text-sm mb-4">History</h3>
            <div className="space-y-3">
              {payouts.length === 0 ? (
                <div className="glass-card rounded-2xl p-6 text-center"><Wallet className="w-6 h-6 text-[#A0A0AB] mx-auto mb-2" /><p className="text-[#A0A0AB] text-xs">No payout history yet.</p></div>
              ) : payouts.map((p) => (
                <div key={p.id} className="glass-card rounded-2xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {p.status === "pending" && <Clock className="w-5 h-5 text-yellow-400" />}
                    {p.status === "approved" && <CheckCircle className="w-5 h-5 text-[#00F0FF]" />}
                    {p.status === "rejected" && <XCircle className="w-5 h-5 text-red-400" />}
                    <div>
                      <p className="text-white font-bold text-sm">${p.amount.toFixed(2)}</p>
                      <p className="text-[#A0A0AB] text-xs">{new Date(p.created_at).toLocaleDateString()} — {p.payment_method}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full capitalize ${p.status === "pending" ? "bg-yellow-400/10 text-yellow-400" : p.status === "approved" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "bg-red-400/10 text-red-400"}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Featured Listing View */}
        {activeView === "featured" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-['Unbounded'] text-lg font-bold text-white mb-2">Boost Your Product</h2>
            <p className="text-[#A0A0AB] text-sm mb-6">Get featured on the homepage and marketplace to reach more buyers.</p>

            {/* Select Product */}
            {!featureProduct ? (
              <div>
                <h3 className="text-white font-semibold text-sm mb-4">Select a product to boost</h3>
                <div className="space-y-3">
                  {products.map((p) => (
                    <button key={p.id} onClick={() => setFeatureProduct(p)} data-testid={`boost-select-${p.id}`}
                      className="glass-card rounded-2xl p-4 w-full text-left flex items-center gap-4 hover:border-[#00F0FF]/30 transition-all">
                      {p.image_url && <img src={p.image_url} alt={p.name} className="w-12 h-12 rounded-xl object-cover" />}
                      <div>
                        <p className="text-white font-semibold text-sm">{p.name}</p>
                        <p className="text-[#A0A0AB] text-xs">{p.product_type} — {p.total_sales || 0} sales</p>
                      </div>
                      {p.is_featured && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <button onClick={() => setFeatureProduct(null)} className="text-[#A0A0AB] hover:text-white"><X className="w-5 h-5" /></button>
                  <p className="text-white font-semibold">Boosting: <span className="text-[#00F0FF]">{featureProduct.name}</span></p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {Object.entries(FEATURED_PACKAGES).map(([id, pkg]) => (
                    <div key={id} data-testid={`featured-pkg-${id}`} className={`glass-card rounded-2xl p-5 relative ${pkg.popular ? "border-yellow-400/30 shadow-[0_0_20px_rgba(250,204,21,0.1)]" : ""}`}>
                      {pkg.popular && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-yellow-400 text-black text-[10px] font-bold px-3 py-0.5 rounded-full">BEST VALUE</div>}
                      <Star className="w-6 h-6 text-yellow-400 mb-3" />
                      <h3 className="text-white font-bold text-sm mb-1">{pkg.name}</h3>
                      <p className="text-[#A0A0AB] text-xs mb-3">{pkg.description}</p>
                      <p className="text-2xl font-black text-white mb-4">${pkg.price}<span className="text-[#A0A0AB] text-sm font-normal">/{pkg.days}d</span></p>
                      <button
                        data-testid={`buy-featured-${id}`}
                        onClick={() => handleFeaturePurchase(id)}
                        className={`w-full py-2.5 rounded-full text-sm font-semibold transition-all ${pkg.popular ? "bg-yellow-400 text-black hover:shadow-[0_0_20px_rgba(250,204,21,0.4)]" : "border border-white/20 text-white hover:border-white/40"}`}
                      >
                        Boost Now
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
